import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Download, FileText, TrendingUp, MessageSquare, Loader2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface TimelineActivity {
  type: "download" | "assessment" | "status_change" | "note";
  date: string;
  description: string;
  metadata?: any;
}

interface LeadActivityTimelineProps {
  leadEmail: string;
  leadId: string;
}

export function LeadActivityTimeline({ leadEmail, leadId }: LeadActivityTimelineProps) {
  const [activities, setActivities] = useState<TimelineActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, [leadEmail, leadId]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const allActivities: TimelineActivity[] = [];

      // Fetch resource downloads
      const { data: downloads } = await supabase
        .from("resource_downloads")
        .select("*")
        .eq("email", leadEmail)
        .order("downloaded_at", { ascending: false });

      if (downloads) {
        downloads.forEach((download) => {
          allActivities.push({
            type: "download",
            date: download.downloaded_at,
            description: `Downloaded "${download.resource_name}"`,
            metadata: { type: download.resource_type },
          });
        });
      }

      // Fetch assessment results
      const { data: assessments } = await supabase
        .from("assessment_results")
        .select("*")
        .eq("email", leadEmail)
        .order("completed_at", { ascending: false });

      if (assessments) {
        assessments.forEach((assessment) => {
          allActivities.push({
            type: "assessment",
            date: assessment.completed_at,
            description: `Completed Security Assessment`,
            metadata: {
              score: assessment.score,
              consultation: assessment.consultation_requested,
            },
          });
        });
      }

      // Fetch status history
      const { data: statusHistory } = await supabase
        .from("lead_status_history")
        .select("*")
        .eq("lead_id", leadId)
        .order("changed_at", { ascending: false });

      if (statusHistory) {
        statusHistory.forEach((history) => {
          allActivities.push({
            type: "status_change",
            date: history.changed_at,
            description: `Status changed from "${history.old_status}" to "${history.new_status}"`,
            metadata: null,
          });
        });
      }

      // Fetch notes
      const { data: notes } = await supabase
        .from("lead_notes")
        .select("*")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });

      if (notes) {
        notes.forEach((note) => {
          allActivities.push({
            type: "note",
            date: note.created_at,
            description: `Note added: ${note.note}`,
            metadata: null,
          });
        });
      }

      // Sort all activities by date
      allActivities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setActivities(allActivities);
    } catch (error) {
      console.error("Error loading activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "download":
        return <Download className="h-4 w-4 text-primary" />;
      case "assessment":
        return <FileText className="h-4 w-4 text-primary" />;
      case "status_change":
        return <TrendingUp className="h-4 w-4 text-primary" />;
      case "note":
        return <MessageSquare className="h-4 w-4 text-primary" />;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No activity recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-4 max-h-96 overflow-y-auto">
      {activities.map((activity, index) => (
        <div key={index}>
          <div className="flex gap-3">
            <div className="mt-1 flex-shrink-0">
              {getActivityIcon(activity.type)}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{activity.description}</p>
              {activity.metadata && activity.type === "assessment" && (
                <div className="text-xs text-muted-foreground mt-1">
                  Score: {activity.metadata.score}/100
                  {activity.metadata.consultation && " • Consultation Requested"}
                </div>
              )}
              {activity.metadata && activity.type === "download" && (
                <div className="text-xs text-muted-foreground mt-1">
                  Type: {activity.metadata.type}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {formatDate(activity.date)}
              </p>
            </div>
          </div>
          {index < activities.length - 1 && <Separator className="mt-4" />}
        </div>
      ))}
    </div>
  );
}
